import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { useAuth } from '@/hooks/useAuth';
import { useChatUnreadCountQuery } from '@/hooks/useChat';
import { CHAT_UNREAD_BADGE_MAXIMUM } from '@/constants/chat';
import { cn } from '@/lib/utils';
import {
  CLIENT_NAVIGATION_ITEMS,
  filterClientNavigationItemsByEnabledModules,
} from './navigationItems';

function buildUnreadBadgeLabel(unreadCount: number): string {
  return unreadCount > CHAT_UNREAD_BADGE_MAXIMUM
    ? String(CHAT_UNREAD_BADGE_MAXIMUM) + '+'
    : String(unreadCount);
}

export function ClientTopNavigation() {
  const { t } = useTranslation(['clients', 'chat']);
  const { enabledModules } = useAuth();
  const chatUnreadCountQuery = useChatUnreadCountQuery('client');
  const chatUnreadCount = chatUnreadCountQuery.data === undefined ? 0 : chatUnreadCountQuery.data;

  const items = filterClientNavigationItemsByEnabledModules(
    CLIENT_NAVIGATION_ITEMS,
    enabledModules
  );

  return (
    <nav className="hidden lg:block bg-zoowhite border-b border-zooborder shrink-0">
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 md:px-4 flex items-center gap-1">
        {items.map(function renderItem(item) {
          return (
            <NavLink
              key={item.id}
              to={item.route}
              end={item.route === APPLICATION_ROUTES.clientPortal}
              className={function resolveClassName({ isActive }) {
                return cn(
                  'relative flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
                  isActive === true
                    ? 'border-zooprimary text-zooprimary'
                    : 'border-transparent text-zoomuted hover:text-zooindigo'
                );
              }}
            >
              <Icon name={item.iconName} />
              {t('clients:' + item.labelKey)}
              {item.unreadBadge === true && chatUnreadCount > 0 ? (
                <span
                  aria-label={t('chat:nav.unreadBadgeAriaLabel', { count: chatUnreadCount })}
                  className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-zooprimary text-zoowhite text-[10px] font-bold"
                >
                  {buildUnreadBadgeLabel(chatUnreadCount)}
                </span>
              ) : null}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
